<template>
    <slot v-if="permissions && ($getPermission(permissions,10).is_granted || $getPermission(permissions,9).is_granted)">
        <section v-if="topEmployees && topEmployees.length > 0 && !loading" class="hidden sm:block sm:ml-24 fixed w-60 text-center border border-sepBorder dark:border-dark.sepBorder rounded h-full mt-2 py-5 pb-36 overflow-auto dark:text-dark.textOnBkg">
            <slot v-if="topEmployees && topEmployees.length > 0">
                <h2 class="font-bold text-2xl">{{ $t('top_employees') }}</h2>
                <span class="font-light">{{topEmployees_month.name}} <font-awesome-icon v-if="1==2" icon="fas fa-angle-down" /></span>
                <MazSelect
                    class="w-36 mt-1"
                    size="sm"
                    :options="lastSixMonths.map(month => ({ label: month.label, value: month.value }))"
                    v-model="selectedTopMonth"
                />
                <div class="grid grid-rows-3 gap-6 w-full mt-5">
                    <div v-for="(t_empl,t_i) in topEmployees" :key="t_i" class="text-center rounded text-center relative">
                        <div class="rounded-full h-36 w-36 border-6 mx-auto" :class="{'border-gold':t_i == 0, 'border-silver':t_i == 1, 'border-bronze':t_i == 2}" :style="'box-shadow: inset 0 0 5px rgba(0,0,0,0.3);'"><font-awesome-icon icon="fa-solid fa-trophy" class="text-7xl pt-8" :class="{'text-gold':t_i == 0, 'text-silver':t_i == 1, 'text-bronze':t_i == 2}" /></div>
                        <div class="p-1 px-3 w-max top-11 text-black absolute left-24 ml-1.5 font-bold text-xl text-center">{{t_i+1}}</div>
                        <h4 class="mt-1.5 w-56 mx-auto truncate">{{t_empl.name}}</h4>
                        <h3 class="font-bold text-2xl">$ {{parseFloat(t_empl.total_sales).toFixed(2)}}</h3>
                    </div>
                </div>
            </slot>
            <font-awesome-icon v-else-if="loading" icon="fas fa-spinner" class="animate-spin absolute top-1/3" />
        </section>

        <div class="mt-1" :class="topEmployees && topEmployees.length > 0 && !loading ? 'sm:ml-86' : 'sm:ml-24'">
            <div class="flex gap-3 mt-2.5 justify-between">
                <div class="flex gap-3">
                    <MazBtn class="h-3" size="sm" v-if="permissions && $getPermission(permissions,9).is_granted" @click="employeeModal = true, employee_pair_id = null">
                        <span class="w-42 text-sm lg:text-base truncate whitespace-nowrap"><font-awesome-icon icon="fa-solid fa-plus" /> {{ $t('add_employee') }}</span>
                    </MazBtn>
                    <MazBtn class="h-3" size="sm" v-if="permissions && $getPermission(permissions,9).is_granted" @click="inviteModal = true">
                        <span class="w-42 text-sm lg:text-base truncate whitespace-nowrap"><font-awesome-icon icon="fa-solid fa-user-plus" /> {{ $t('invite_via_email') }} (BETA)</span>
                    </MazBtn>
                    <MazBtn class="h-3" size="sm" @click="refreshEmployees()">
                        <span class="w-42 text-sm lg:text-base truncate whitespace-nowrap">
                            <font-awesome-icon icon="fa-solid fa-refresh" />
                        {{ $t('refresh') }}</span>
                    </MazBtn>
                    <MazInput 
                        v-model="searchQuery" 
                        :placeholder="$t('search_by_name')" 
                        size="sm"
                        clearable
                    />
                </div>
                <div class="relative mr-2">
                    <button @click="showColumnDropdown = !showColumnDropdown" class="bg-mazUIBg text-mazUiText dark:bg-dark.mazUIBg dark:text-dark.mazUiText rounded-xl cursor-pointer p-2.5 px-3 border border-sepBorder dark:border-dark.mazUIBg text-center whitespace-nowrap text-xs">
                        {{ $t('show_hide') }} <font-awesome-icon :icon="['fas', 'angle-down']" />
                    </button>
                    <div v-if="showColumnDropdown" class="absolute z-10 bg-white bg-mazUIBg text-mazUiText dark:bg-dark.mazUIBg dark:text-dark.mazUiText rounded-lg shadow-lg p-2 mt-1 right-0">
                        <div v-for="column in togglableColumns" :key="column.name" class="flex items-center">
                        <input type="checkbox" :id="column.name" v-model="columnVisibility[column.name]" class="mr-2" />
                        <label :for="column.name">{{ column.label }}</label>
                        </div>
                    </div>
                </div>
            </div>
            <button v-if="permissions && $getPermission(permissions,11).is_granted" @click="showActive = !showActive" class="block ml-3 sm:ml-0 mt-2 rounded outline-0 cursor-pointer text-button dark:text-growth text-center text-sm">
                <font-awesome-icon :icon="showActive ? 'fas fa-check-square' : 'far fa-square'" /> {{ $t('show_active_employees') }}
            </button>

            <div class="overflow-auto pr-3 pb-10" style="height: calc(100vh - 130px);" v-if="permissions && $getPermission(permissions, 10).is_granted">
                <table v-if="filteredEmployees.length>0 && !loading" class="bg-tableBG dark:bg-dark.tableBG mt-3 mx-auto whitespace-nowrap rounded font-normal border border-sepBorder dark:border-dark.sepBorder table-auto w-full dark:text-dark.textOnBkg">
                <thead>
                    <tr class="dark:bg-dark.panels">
                        <th class="w-42 pb-2 text-left px-2 py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('name') }}</th>
                        <th v-if="columnVisibility.designation" class="pb-2 px-2 w-42 text-left py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('designation') }}</th>
                        <th v-if="columnVisibility.assignment" class="w-42 pb-2 text-left px-2.5 py-3 border-r border-sepBorder dark:border-dark.sepBorder text-left">{{ $t('assignment') }}</th>
                        <th v-if="columnVisibility.sales" class="pb-2 px-4 text-left py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('sales') }}</th>
                        <th v-if="columnVisibility.hours" class="pb-2 px-4 text-left py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('hours') }}</th>
                        <th v-if="columnVisibility.shifts" class="pb-2 px-4 text-left py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('Shifts') }}</th>
                        <th v-if="columnVisibility.avgSalesShift" class="pb-2 px-4 text-left py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('avg_sales_shift') }}</th>
                        <th v-if="columnVisibility.avgWPM" class="pb-2 px-4 text-left py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('avg_wpm') }}</th>
                        <th v-if="columnVisibility.avgCPM" class="pb-2 px-4 text-left py-3 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('avg_cpm') }}</th>
                        <th class="pb-2 px-4 text-center py-3">{{ $t('actions') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(employee, index) in filteredEmployees" :key="employee.id" class="cursor-pointer hover:bg-gray-200 hover:dark:bg-dark.panels" :class="{'bg-gray-100 dark:bg-dark.tableRow1': index%2 == 1}" @click="editEmployee(employee.id)">
                    <td class="p-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder border-l-0 leading-5">
                        <div class="flex items-center">
                            <div class="rounded-full h-5 w-5 shadow bg-gray-200 mr-2" :style="{ backgroundColor: employee.color, backgroundSize: 'cover', backgroundPosition: 'center' }"></div>
                            <div class="font-semibold whitespace-nowrap">{{employee.name}}</div>
                        </div>
                    </td>
                    <td v-if="columnVisibility.designation" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-left capitalize">{{ employee.positionName.replace("_", " ") }}</td>
                    <td v-if="columnVisibility.assignment" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-left">
                        {{ employee.groupName }} {{ employee.teamName ? ' - ' + employee.teamName : null }}
                    </td>
                        <td v-if="columnVisibility.sales" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-left">{{ employee.total_sales ? '$ ' + employee.total_sales.toFixed(2) : "-"}}</td>
                        <td v-if="columnVisibility.hours" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-center">{{ employee.hours ?? "-"}}</td>
                        <td v-if="columnVisibility.shifts" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-center">{{ employee.shifts ?? "-"}}</td>
                        <td v-if="columnVisibility.avgSalesShift" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-center">{{ employee.total_sales ? '$ ' + (parseFloat(employee.total_sales) / parseFloat(employee.shifts)).toFixed(2) : "-"}}</td>
                        <td v-if="columnVisibility.avgWPM" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-center">{{ employee.wpm }}</td>
                        <td v-if="columnVisibility.avgCPM" class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder text-center">{{ employee.cpm }}</td>
                    <td class="px-2.5 border-t border-b border-sepBorder dark:border-dark.sepBorder border-sepBorder dark:border-dark.sepBorder text-center text-button">
                        <slot v-if="((parseInt(employee.orderNo) > parseInt(myself?.orderNo)) || (myself?.orderNo == 1 && (parseInt(employee.orderNo) >= parseInt(myself?.orderNo))) || (employee.id == myself?.id)) && employee.active == 1">
                            <span v-if="employee.suspended == 1" class="px-2 cursor-pointer text-center" title="Unsuspend the account" @click.stop="emplSuspend(0,employee.id)">
                                <font-awesome-icon icon="fas fa-circle-play" class="text-red-500" />
                            </span>
                            <span v-else class="px-2 cursor-pointer text-center" title="Suspend the account" @click.stop="emplSuspend(1,employee.id)">
                                <font-awesome-icon icon="fas fa-circle-pause" class="text-green-500" />
                            </span>
                        </slot>
                        <slot v-if="((parseInt(employee.orderNo) > parseInt(myself?.orderNo)) || (myself?.orderNo == 1 && (parseInt(employee.orderNo) >= parseInt(myself?.orderNo))) || (employee.id == myself?.id)) && employee.suspended == 1 || employee.active == 0">
                            <span v-if="employee.active == 0" class="px-2 cursor-pointer text-center text-sm" title="Restore from archive" @click.stop="emplArchive(1,employee.id)">
                                Restore <font-awesome-icon icon="fas fa-undo" />
                            </span>
                            <span class="px-2 cursor-pointer text-center" title="Archive the account" @click.stop="emplArchive(0,employee.id)" >
                                <font-awesome-icon icon="fas fa-archive" />
                            </span>
                        </slot>
                    </td>
                    </tr>
                </tbody>
                </table>
                <div v-else-if="loading" class="text-center mt-10 dark:text-dark.textOnBkg">
                    <font-awesome-icon icon="fas fa-spinner" class="animate-spin text-xl" />
                </div>
                <div v-else>
                    <h1 class="text-center text-2xl font-semibold text-contentText dark:text-dark.textOnBkg my-16">{{ $t('no_employees_yet') }}</h1>
                    <img src="@/assets/images/undraw_job-offers_55y0.svg" class="w-full sm:h-96 mx-auto mt-16 mb-10 px-3 sm:px-0" />
                </div>
            </div>
            <div v-else class="mt-36 text-center dark:text-dark.textOnBkg">{{ $t('no_permission') }}</div>

            <transition-group name="zoom-fade">
                <section v-if="employeeModal" @click="employeeModal = false; $router.replace({'query': null});" class="backdrop-blur w-full h-full fixed inset-0 bg-gray-700 bg-opacity-30 z-50 flex justify-center items-center px-4 text-black">
                    <section @click.stop class="bg-white dark:bg-dark.bkg dark:text-dark.textOnBkg rounded-lg w-full h-[calc(100vh-4rem)] sm:max-w-7xl my-8 mx-auto p-8 sm:p-12 z-50 overflow-auto relative">
                        <font-awesome-icon @click="employeeModal = false; $router.replace({'query': null});" icon="fas fa-xmark" class="transition absolute p-6 top-0 right-0 text-xl cursor-pointer hover:opacity-50" />
                        <addEmployee :noarrow="false" :employee_id_prop="employee_pair_id" @close="handleClose" />
                    </section>
                </section>
                <section v-if="inviteModal">
                    <section @click="inviteModal = false" class="backdrop-blur w-full h-full fixed inset-0 bg-gray-700 bg-opacity-30 z-50 flex justify-center items-center px-4 text-black">
                        <section @click.stop class="bg-white dark:bg-dark.bkg dark:text-dark.textOnBkg rounded-lg w-full max-h-screen max-w-md sm:max-w-7xl mx-auto my-auto p-8 sm:p-12 z-50 overflow-auto relative">
                            <font-awesome-icon @click="inviteModal = false" icon="fas fa-xmark" class="transition absolute p-6 top-0 right-0 text-xl cursor-pointer hover:opacity-50" />
                            <inviteEmployee />
                        </section>
                    </section>
                </section>
            </transition-group>
        </div>
    </slot>
    <div v-else class="mt-36 text-center dark:text-dark.textOnBkg">{{ $t('no_permission') }}</div>
</template>

<script setup>
    import addEmployee from "@/components/settings/employees/employeesForm.vue"
    import inviteEmployee from "@/components/settings/employees/inviteEmployee.vue"
</script>

<script>
    import { useAuthStore } from '@/stores/auth';
    import { get_users, suspend_user, archive_user, get_top_employee } from '@/api/api';
    export default {
        components() {
            addEmployee
        },
        data() {
            return {
                employeeModal: false,
                employeeData: [],
                employee_pair_id: null,
                loading:true,
                topEmployees:[],
                showActive:true,
                selectedTopMonth: null,
                topEmployees_month: {name: null, value: null},
                inviteModal: false,
                showColumnDropdown: false,
                columnVisibility: {
                    designation: true,
                    assignment: true,
                    access_type: true,
                    sales: true,
                    hours: true,
                    shifts: true,
                    avgSalesShift: true,
                    avgWPM: true,
                    avgCPM: true,
                },
                searchQuery: '', // Add search query state
            }
        },
        watch: {
            selectedTopMonth: function (o,n) {
                this.getTopEmployees();
            },
        },
        computed: {
            authStore() {
                return useAuthStore();
            },
            myself() {
                return this.authStore.myself;
            },
            permissions() {
                return this.authStore.permissions;
            },
            togglableColumns() {
                return [
                    { name: 'id', label: this.$t('id') },
                    { name: 'tag', label: this.$t('tag') },
                    { name: 'designation', label: this.$t('designation') },
                    { name: 'access_type', label: this.$t('access_type') },
                    { name: 'assignment', label: this.$t('assignment') },
                    { name: 'sales', label: this.$t('sales') },
                    { name: 'hours', label: this.$t('hours') },
                    { name: 'shifts', label: this.$t('Shifts') },
                    { name: 'avgSalesShift', label: this.$t('avg_sales_shift') },
                    { name: 'avgWPM', label: this.$t('avg_wpm') },
                    { name: 'avgCPM', label: this.$t('avg_cpm') },
                ];
            },
            lastSixMonths() {
                let months = [];
                for (let i = 0; i < 6; i++) {
                    let month = this.$moment().subtract(i, 'months');
                    months.push({
                    value: month.format('MM-YYYY'),
                    label: month.format('MMMM YYYY')
                    });
                }
                return months;
            },
            employeeDataFiltered() {
                if (this.employeeData) {
                    if (this.showActive == true) {
                        return this.employeeData.filter(employee => employee.active == 1)
                    } else {
                        return this.employeeData;
                    }
                } else {
                    return [];
                }
            },
            filteredEmployees() {
                return this.employeeDataFiltered.filter(employee => 
                    employee.name.toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            }
        },
        methods: {
            refreshEmployees() {
                this.loading = true;
                this.getEmployeeData();
            },
            handleClose() {
                this.employeeModal = false;
                this.getEmployeeData();
            },
            async emplArchive(action, employeeId) {
                if (confirm(this.$t('you_sure') + (action == 0 ? this.$t('archive_user') : this.$t('unarchive_user')))) { 
                    const employee = this.employeeData.find(emp => emp.id === employeeId);
                    if (employee) {
                        employee.active = action;
                        await archive_user(this.$cookies.get('userToken'),this.$cookies.get('c_id'),action, employeeId);
                    }
                }
            },
            async emplSuspend(action, employeeId) {
                if (confirm(this.$t('you_sure') + (action == 1 ? this.$t('suspend') : this.$t('unsuspend')) + " " + this.$t('this_user') + "?")) {
                    const employee = this.employeeData.find(emp => emp.id === employeeId);
                    if (employee) {
                        employee.suspended = action;
                        await suspend_user(this.$cookies.get('userToken'), this.$cookies.get('c_id'), action, employeeId);
                    }
                }
            },
            editEmployee(pair_id = null) {
                if (this.permissions && !this.$getPermission(this.permissions,10).is_granted) {return;}
                this.$router.push({name: 'Employees', query: {'id': pair_id,'e':true} }), 
                this.employee_pair_id = pair_id;
                this.employeeModal = true;
            },
            async getEmployeeData() {
                let activeEmployees = await get_users(this.$cookies.get('userToken'),this.$cookies.get('c_id'),1,null,null,null,"0",null,null,1);
                if (this.myself) {
                    activeEmployees = activeEmployees.filter(employee => employee.id != this.myself.id);
                }
                let archivedEmployees = [];
                if (this.permissions && this.$getPermission(this.permissions,11).is_granted) {
                    archivedEmployees = await get_users(this.$cookies.get('userToken'),this.$cookies.get('c_id'),0,null,null,null,"0",null,null,1);
                }
                this.employeeData = [...(Array.isArray(activeEmployees) ? activeEmployees : []), ...(Array.isArray(archivedEmployees) ? archivedEmployees : [])];
                this.loading = false;
            },
            async getTopEmployees() {
                let date = this.$moment(this.selectedTopMonth, "MM-YYYY");
                let startOfMonth = date.startOf('month').format('YYYY-MM-DD');
                let endOfMonth = date.endOf('month').format('YYYY-MM-DD');
                this.topEmployees = await get_top_employee(this.$cookies.get('userToken'),this.$cookies.get('c_id'), startOfMonth, endOfMonth,3);
            },
        },
        async mounted() {            
            const currentDate = new Date();
            currentDate.setMonth(currentDate.getMonth() - 1); // Subtract 1 month from the current date
            const month = currentDate.getMonth() + 1; // Adding 1 to get month in range 1-12

            this.selectedTopMonth = `${month.toString().padStart(2, '0')}-${currentDate.getFullYear()}`;

            this.getEmployeeData();
            if (this.selectedTopMonth) {
                this.getTopEmployees();
            }

            //check the route and open the modal if needed employees?id=653&e=true check if the id is set and open the modal
            if (this.$route.query.id && this.$route.query.e) {
                this.employeeModal = true;
                this.employee_pair_id = this.$route.query.id;
            }
        }
    }
</script>

<style scoped>
    .zoom-fade-enter-active {
        transition: all .3s ease;
    }
    .zoom-fade-leave-active {
        transition: all .3s ease;
    }
    .zoom-fade-enter-from, .zoom-fade-leave-to {
        transform: scale(0.9);
        opacity: 0;
    }
</style>